import React, { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, Clock, CheckCircle, Target } from "lucide-react";
import { ConversationMetrics as Metrics } from "../types";
import { cn } from "@/lib/utils";

interface ConversationMetricsProps {
  metrics: Metrics;
  defaultExpanded?: boolean;
}

export const ConversationMetrics: React.FC<ConversationMetricsProps> = ({
  metrics,
  defaultExpanded = false,
}) => {
  const { t } = useTranslation();
  const panelId = useId();
  const [expanded, setExpanded] = useState(defaultExpanded);

  const metricsData = [
    {
      icon: Clock,
      label: t("conversations.metrics.avgResponseTime"),
      value: metrics.averageResponseTime,
      color: "text-blue-500",
    },
    {
      icon: CheckCircle,
      label: t("conversations.metrics.responseRate"),
      value: `${metrics.responseRate}%`,
      color: "text-green-500",
    },
    {
      icon: Target,
      label: t("conversations.metrics.conversionRate"),
      value: `${metrics.conversionRate}%`,
      color: "text-purple-500",
    },
  ];

  return (
    <div className="border-t border-border bg-card flex-shrink-0">
      <button
        type="button"
        id={`${panelId}-trigger`}
        className="w-full flex items-center gap-2 p-3 text-left hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={expanded ? t("conversations.metrics.collapse") : t("conversations.metrics.expand")}
      >
        <div className="flex-1 min-w-0">
          <h3 className="text-sm font-semibold text-foreground">{t("conversations.metrics.title")}</h3>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">
            {t("conversations.metrics.subtitle")}
          </p>
        </div>
        <ChevronDown
          className={cn("h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-200", expanded && "rotate-180")}
          strokeWidth={1.5}
          aria-hidden
        />
      </button>
      {expanded && (
        <div id={panelId} className="px-4 pb-4 pt-0 space-y-3" role="region" aria-labelledby={`${panelId}-trigger`}>
          {metricsData.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <div key={index} className="flex items-center gap-3">
                <Icon className={`h-4 w-4 ${metric.color}`} strokeWidth={1.5} />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground truncate">{metric.label}</p>
                  <p className="text-sm font-semibold text-foreground">{metric.value}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
